/* eslint-disable react/no-unstable-nested-components */
import {
  Box,
  Button,
  Header,
  Select,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { travelerFromFirestore } from '@parallel-fluidics/shared/models/travelers';
import { checkIfAutoTransferable } from '@parallel-fluidics/shared/utils';
import ScanField from 'features/scan/components/ScanField';
import { ScanTypes } from 'features/scan/utils/parseScanCode';
import {
  collection,
  documentId,
  getFirestore,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

function TravelersDisplay({ travelers, setTravelers }) {
  const [travelerData, setTravelerData] = useState([]);
  const [status, setStatus] = useState('loading');
  const [travelersWarningMap, setTravelersWarningMap] = useState({});

  const {
    values: {
      project = {}, tool = {}, toolMap = {}, process, selectedStep,
    },
    errors: { travelers: travelersErrorMap, toolMap: toolMapError },
    setFieldError,
    setFieldValue,
  } = useFormikContext();

  useEffect(() => {
    if (!travelers?.length) return undefined;
    const unsubscribe = onSnapshot(
      query(
        collection(getFirestore(), 'travelers'),
        where(documentId(), 'in', travelers),
      ),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => travelerFromFirestore(doc));
        setTravelerData(data);
        setStatus('success');
      },
      () => {
        setStatus('error');
      },
    );

    return () => unsubscribe();
  }, [travelers]);

  // validate existing travelers matches project id
  useEffect(() => {
    if (!project?.projectId) return;

    const errorMap = {};
    const warningMap = {};

    travelerData?.forEach((traveler) => {
      // no need to check lineItemId, as we may have Tuning travelers from other orders
      if (traveler.project !== project.projectId) {
        errorMap[traveler.id] = `This traveler belongs to project ${traveler.project}, which does not match the selected project ${project.projectId}.`;
        return;
      }

      const isAutoTransferable = checkIfAutoTransferable({
        from: {
          projectId: traveler.project,
          lineItemId: traveler.lineItem,
          steps: traveler.steps,
        },
        target: {
          projectId: project.projectId,
          lineItemId: project.lineItemId,
          steps: project.steps,
        },
        currentStepIndex: selectedStep?.value || 0,
      });
      if (isAutoTransferable) {
        warningMap[traveler.id] = (
          <span>
            By clicking
            {' '}
            <strong>Start</strong>
            , this traveler will be automatically transferred to the line item
            {' '}
            <strong>{`${project.projectId}-${project.pPartId}`}</strong>
            , are you sure you want to proceed?
          </span>
        );
        return;
      }
      // if cannot auto transfer, we need to check pPartId match
      if (traveler.pPart !== project.pPartId) {
        errorMap[traveler.id] = 'This traveler does not match the selected project. Please use another traveler.';
      }
    });

    if (Object.keys(errorMap).length > 0) {
      setFieldError('travelers', errorMap);
    } else {
      setFieldError('travelers', null);
    }

    if (Object.keys(warningMap).length > 0) {
      setTravelersWarningMap(warningMap);
    } else {
      setTravelersWarningMap(null);
    }
  }, [project, selectedStep?.value, setFieldError, travelerData]);

  const travelerItems = useMemo(() => {
    if (status === 'loading') {
      return travelers.map((traveler) => ({
        label: traveler,
        description: 'Loading info...',
      }));
    }
    const travelerMap = new Map(travelerData.map((traveler) => [traveler.id, traveler]));
    return travelers.map((traveler) => {
      if (!travelerMap.has(traveler)) {
        return {
          label: traveler,
          description: 'New traveler!',
        };
      }
      const { steps, pPart, project: projectId } = travelerMap.get(traveler);
      let description = 'Existing traveler';
      if (pPart && projectId) {
        description += ` (${projectId}-${pPart})`;
      }
      // loop through steps and add to description
      steps.forEach((step) => {
        if (step.runId?.length > 0) {
          description += ` | ${step.name} run${step.runId.length > 1 ? 's' : ''}: ${step.runId.map((run) => `${run.project} - ${run.runID}`).join(', ')}`;
        }
      });
      return {
        label: traveler,
        description,
      };
    });
  }, [status, travelerData, travelers]);

  return (
    <Table
      columnDefinitions={[
        {
          id: 'label',
          header: (
            <Header
              description={<Box color="text-status-error" fontSize="body-s">{toolMapError}</Box>}
            >
              Traveler(s)
            </Header>
          ),
          cell: (item) => (
            <>
              <div>{item.label}</div>
              <Box color="text-status-inactive" fontSize="body-s">{item.description}</Box>
              {travelersErrorMap?.[item.label] && (
                <Box color="text-status-error" fontSize="body-s">{travelersErrorMap[item.label]}</Box>
              )}
              {travelersWarningMap?.[item.label] && (
                <Box color="text-status-warning" fontSize="body-s">{travelersWarningMap[item.label]}</Box>
              )}
            </>
          ),
          isRowHeader: true,
        },
        ...(process === 'forming' ? [
          {
            id: 'cavity',
            header: 'Cavity',
            cell: (item) => (
              <Select
                selectedOption={
                  toolMap[item.label]
                    ? { label: toolMap[item.label], value: toolMap[item.label] }
                    : null
                }
                onChange={({ detail }) => {
                  setFieldValue(`toolMap.${item.label}`, detail.selectedOption.value);
                }}
                placeholder="Choose a cavity"
                expandToViewport
                options={tool.cavities?.map((cavity) => (
                  { label: cavity.name, value: cavity.name }
                ))}
                invalid={!!toolMapError && !toolMap?.[item.label]}
              />
            ),
          },
        ] : []),
        {
          id: 'remove',
          header: '',
          cell: (item) => (
            <Button
              variant="icon"
              iconName="close"
              onClick={() => {
                if (travelers.length === 1) setFieldError('travelers', null);
                setTravelers(travelers.filter((t) => t !== item.label));
              }}
            />
          ),
        },
      ]}
      items={travelerItems}
    />
  );
}

TravelersDisplay.propTypes = {
  travelers: PropTypes.arrayOf(PropTypes.string).isRequired,
  setTravelers: PropTypes.func.isRequired,
};

function TravelerInput({
  id, travelers = [], errorText = '',
}) {
  const { values: { tool, toolMap }, setFieldValue } = useFormikContext();

  const handleSetTravelers = useCallback((newTravelers) => {
    setFieldValue('travelers', newTravelers);
    setFieldValue('numberOfChips', newTravelers.length);
    // make a shallow copy of toolMap,
    // we will need this to auto assign cavities and remove unused ones
    const toolMapCopy = { ...toolMap };
    if (tool.cavities?.length > 0) {
      newTravelers.forEach((traveler, index) => {
        if (!toolMapCopy[traveler] && index < tool.cavities.length) {
          toolMapCopy[traveler] = tool.cavities[index].name;
        }
      });
    }
    // remove toolMap entries for travelers that are no longer in the list
    setFieldValue(
      'toolMap',
      Object.fromEntries(
        Object.entries(toolMapCopy).filter(([traveler]) => newTravelers.includes(traveler)),
      ),
    );
  }, [setFieldValue, tool?.cavities, toolMap]);

  const handleScan = useCallback((travelerId) => {
    if (!travelers.includes(travelerId)) {
      handleSetTravelers([...travelers, travelerId]);
    }
  }, [handleSetTravelers, travelers]);

  return (
    <SpaceBetween direction="vertical" size="s">
      <ScanField
        id={id}
        label="Traveler(s)"
        errorText={errorText}
        accept={[ScanTypes.TRAVELER]}
        onScan={({ id: travelerId }) => handleScan(travelerId)}
        closeOnScan
      />
      {travelers.length > 0
        ? <TravelersDisplay travelers={travelers} setTravelers={handleSetTravelers} />
        : null}
    </SpaceBetween>
  );
}

TravelerInput.propTypes = {
  id: PropTypes.string.isRequired,
  travelers: PropTypes.arrayOf(PropTypes.string),
  errorText: PropTypes.string,
};

export default TravelerInput;
